import { Component, ChangeDetectionStrategy, inject, signal, computed } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslationService } from '../../services/translation.service';
import { AnimateOnScrollDirective } from '../../directives/animate-on-scroll.directive';

@Component({
  selector: 'app-contact',
  templateUrl: './contact.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, AnimateOnScrollDirective]
})
export class ContactComponent {
  private translationService = inject(TranslationService);
  t = computed(() => this.translationService.t().contact);

  isSuccess = signal(false);
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  
  showFallbackButton = signal(false);
  fallbackMailtoLink = signal('');

  private readonly endpoint = 'https://api.web3forms.com/submit';
  
  // Váš Access Key.
  private readonly accessKey: string = 'e8d8213b-2ea7-40ad-ad2e-c79c23a2454e'; 

  async onSubmit(form: NgForm): Promise<void> {
    if (form.invalid) {
      return;
    }

    const formData = form.value;

    // KONTROLA: Ak kľúč chýba alebo je to len text "SEM_VLOZTE...", otvoríme mailového klienta.
    // Inak sa pokúsime odoslať formulár cez API.
    if (!this.accessKey || this.accessKey === 'SEM_VLOZTE_VAS_ACCESS_KEY') {
        this.openMailClientDirectly(formData);
        this.isSuccess.set(true);
        form.resetForm();
        return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.showFallbackButton.set(false);

    const payload = {
      access_key: this.accessKey,
      name: formData.name,
      email: formData.email,
      message: formData.message,
      subject: 'Nová správa z webu Milok Gerüstbau',
      from_name: 'Milok Web Form',
      botcheck: false
    };

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        this.isSuccess.set(true);
        form.resetForm();
      } else {
        console.error('Web3Forms error:', data);
        // Ak API zlyhá (napr. zlý kľúč), zobrazíme chybovú hlášku a tlačidlo na manuálne odoslanie
        const message = data.message ? data.message : this.t().formErrorServer;
        this.errorMessage.set(message);
        this.prepareFallback(formData);
      }
    } catch (error) {
      console.error('Network error:', error);
      this.errorMessage.set(this.t().formErrorNetwork);
      this.prepareFallback(formData);
    } finally {
      this.isLoading.set(false);
    }
  }

  prepareFallback(data: any): void {
    const subject = encodeURIComponent('Nová správa z webu Milok');
    const body = encodeURIComponent(
      `Meno: ${data.name}\nEmail: ${data.email}\n\nSpráva:\n${data.message}`
    );
    this.fallbackMailtoLink.set(`mailto:office.milok@gmail.com?subject=${subject}&body=${body}`);
    this.showFallbackButton.set(true);
  }

  openMailClientDirectly(data: any): void {
    const subject = encodeURIComponent('Nová správa z webu Milok');
    const body = encodeURIComponent(
      `Meno: ${data.name}\nEmail: ${data.email}\n\nSpráva:\n${data.message}`
    );
    const link = `mailto:office.milok@gmail.com?subject=${subject}&body=${body}`;
    
    window.location.href = link;
  }

  resetForm(): void {
    this.isSuccess.set(false);
    this.errorMessage.set(null);
    this.showFallbackButton.set(false);
  }
}